import React from "react";

import { Box, Button, Stack, Typography, ToggleButton, ToggleButtonGroup, Paper } from "@mui/material";

import { LanguageContext, LANGUAGE_OPTIONS, applyLanguageCode, useLanguage } from "../../config/language/language";
import * as english from "../../config/language/english";
import * as estonian from "../../config/language/estonian";
import {
    DEFAULT_GUEST_PREFS,
    TASTE_OPTIONS,
    VENUES,
    normalizeGuestPrefs,
    normalizeVenue
} from "../../helper-functions/shared/guestPrefs";

const LANG_PACKS = { en: english, et: estonian };

const TASTE_LABEL_KEYS = {
    bitter: "PREFS_TASTE_BITTER",
    tart: "PREFS_TASTE_TART",
    sweet: "PREFS_TASTE_SWEET",
    fruity: "PREFS_TASTE_FRUITY",
    citrusy: "PREFS_TASTE_CITRUSY",
    herbal: "PREFS_TASTE_HERBAL",
    floral: "PREFS_TASTE_FLORAL",
    creamy: "PREFS_TASTE_CREAMY",
    nutty: "PREFS_TASTE_NUTTY",
    spicy: "PREFS_TASTE_SPICY",
    refreshing: "PREFS_TASTE_REFRESHING",
    boozy: "PREFS_TASTE_BOOZY",
    light: "PREFS_TASTE_LIGHT",
    dry: "PREFS_TASTE_DRY",
    smoky: "PREFS_TASTE_SMOKY",
    tropical: "PREFS_TASTE_TROPICAL",
    bubbly: "PREFS_TASTE_BUBBLY"
};

/**
 * First-visit landing (and /preferences editor): language, drink type, tastes, venue.
 */
export default function WelcomePrefs(props) {
    const { language: ctxLanguage, setLanguage: setCtxLanguage } = React.useContext(LanguageContext);
    const LANG = useLanguage();
    const existing = normalizeGuestPrefs(props.prefs?.guestPrefs || DEFAULT_GUEST_PREFS);
    const isEdit = Boolean(existing.completed) && props.mode === "edit";

    const [language, setLanguage] = React.useState(existing.language || ctxLanguage || "en");
    const [drinkType, setDrinkType] = React.useState(existing.drinkType || "alcoholic");
    const [tastes, setTastes] = React.useState(existing.tastes || []);
    const [venue, setVenue] = React.useState(existing.venue || "Tallinn");

    // Live UI language while choosing on the landing page
    React.useEffect(() => {
        if (language) {
            applyLanguageCode(language);
            setCtxLanguage(language);
        }
    }, [language, setCtxLanguage]);

    const canContinue = Boolean(language && drinkType);

    const savePrefs = () => {
        const next = {
            completed: true,
            language,
            drinkType,
            tastes: tastes.filter((t) => TASTE_OPTIONS.includes(t)),
            venue: normalizeVenue(venue)
        };
        props.prefs.setGuestPrefs(next);
        setCtxLanguage(language);
        return next;
    };

    const handleSaveEdit = () => {
        if (!canContinue) return;
        savePrefs();
        props.browser.navigate("/");
    };

    const handleGoMenu = () => {
        if (!canContinue) return;
        savePrefs();
        props.browser.navigate("/");
    };

    const handleAskBartender = () => {
        if (!canContinue) return;
        const saved = savePrefs();
        // Use the language just chosen (not only context) so ET prompts are correct
        const pack = LANG_PACKS[saved.language] || LANG;
        const drinkTypeLabel =
            saved.drinkType === "nonAlcoholic" ? pack.PREFS_NON_ALCOHOLIC : pack.PREFS_ALCOHOLIC;
        const tasteLabels = saved.tastes.map((t) => pack[TASTE_LABEL_KEYS[t]] || t);
        const autoPrompt = pack.chatWelcomeRecommendPrompt({ drinkTypeLabel, tasteLabels });
        props.browser.navigate("/chat", {
            autoPrompt,
            autoAskId: Date.now()
        });
    };

    return (
        <Box
            sx={{
                minHeight: isEdit ? "auto" : "100vh",
                display: "flex",
                alignItems: isEdit ? "flex-start" : "center",
                justifyContent: "center",
                px: 2,
                py: isEdit ? 2 : 4,
                background: isEdit
                    ? "transparent"
                    : (theme) =>
                          theme.palette.mode === "dark"
                              ? "radial-gradient(ellipse at top, #2a3d5c 0%, #121820 55%)"
                              : "radial-gradient(ellipse at top, #e8eef6 0%, #f7f4ef 55%)"
            }}>
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    maxWidth: 480,
                    p: { xs: 3, sm: 4 },
                    borderRadius: 3,
                    bgcolor: "background.paper",
                    border: 1,
                    borderColor: "divider"
                }}>
                <Stack spacing={3} alignItems="stretch">
                    <Box textAlign="center">
                        <Typography variant="h3" sx={{ fontFamily: "Fraunces, serif", mb: 1 }}>
                            {LANG.WEBSITE_NAME}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {isEdit ? LANG.PREFS_EDIT_SUBTITLE : LANG.PREFS_WELCOME_SUBTITLE}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {LANG.PREFS_LANGUAGE}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            value={language}
                            onChange={(_, next) => next && setLanguage(next)}>
                            {LANGUAGE_OPTIONS.map((opt) => (
                                <ToggleButton key={opt.code} value={opt.code}>
                                    {opt.name}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            {LANG.PREFS_DRINK_TYPE}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            value={drinkType}
                            onChange={(_, next) => next && setDrinkType(next)}
                            orientation="vertical">
                            <ToggleButton value="alcoholic">{LANG.PREFS_ALCOHOLIC}</ToggleButton>
                            <ToggleButton value="nonAlcoholic">{LANG.PREFS_NON_ALCOHOLIC}</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            {LANG.PREFS_TASTES}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {LANG.PREFS_TASTES_HINT}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {TASTE_OPTIONS.map((taste) => (
                                <ToggleButton
                                    key={taste}
                                    value={taste}
                                    size="small"
                                    selected={tastes.includes(taste)}
                                    onChange={() => {
                                        setTastes((prev) =>
                                            prev.includes(taste) ? prev.filter((t) => t !== taste) : [...prev, taste]
                                        );
                                    }}
                                    sx={{ textTransform: "none", borderRadius: 2, px: 1.5 }}>
                                    {LANG[TASTE_LABEL_KEYS[taste]] || taste}
                                </ToggleButton>
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                            {LANG.PREFS_VENUE}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {LANG.PREFS_VENUE_HINT}
                        </Typography>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            value={venue}
                            onChange={(_, next) => next && setVenue(next)}>
                            {VENUES.map((v) => (
                                <ToggleButton key={v} value={v}>
                                    {v}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>

                    {isEdit ? (
                        <>
                            <Button variant="contained" size="large" disabled={!canContinue} onClick={handleSaveEdit}>
                                {LANG.PREFS_SAVE}
                            </Button>
                            <Button variant="text" onClick={() => props.browser.navigate("/")}>
                                {LANG.BUTTON_CANCEL}
                            </Button>
                        </>
                    ) : (
                        <Stack spacing={1.25}>
                            <Button variant="contained" size="large" disabled={!canContinue} onClick={handleAskBartender}>
                                {LANG.PREFS_ASK_BARTENDER}
                            </Button>
                            <Button variant="outlined" size="large" disabled={!canContinue} onClick={handleGoMenu}>
                                {LANG.PREFS_GO_MENU}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Paper>
        </Box>
    );
}
